import { ApolloClient } from "@apollo/client";
import { isEqual } from "lodash";
import { AgGrid, toast } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { GetAccountingPayablesDocument } from "#graphql/supergraphHooks";
import {
  type AccountingPayableDateRangeInput,
  type AccountingPayableFilters,
  type AccountingPayableSort,
  type GetAccountingPayablesQuery,
} from "#graphql/supergraphTypes";
import { clientContext } from "#utils/graphql_utils";
import { calculateCursorFromOffset } from "../DistributionBatchesTable/distributionBatchesGQLDataSource";
import { type DateRangeFilterModel } from "../GridFilters/DateRangeFilter/types";
import { type PaymentTypeFilterModel } from "../GridFilters/PaymentTypeFilter";
import { SupportedColumns } from "./columnDefinitions";
import { toTransactionRowItem } from "./utils";

/**
 * Maps an AG Grid column ID to the corresponding GraphQL sort field.
 * Returns undefined if the column is not sortable on the backend.
 */
const AG_GRID_COL_TO_SORT_FIELD: Record<string, AccountingPayableSort["field"]> = {
  [SupportedColumns.TransactionDate]: "TRANSACTION_DATE",
  [SupportedColumns.RequestedPaymentDate]: "REQUESTED_PAYMENT_DATE",
};

/**
 * Builds the GraphQL sort input from AG Grid's sort model.
 * Only the first sort column is used (single-column sort).
 * Returns undefined when no valid sort is provided (backend uses default sort).
 */
export const buildAccountingPayableSort = (
  sortModel?: AgGrid.SortModelItem[] | null,
): AccountingPayableSort | undefined => {
  if (!sortModel || sortModel.length === 0) {
    return undefined;
  }

  const sort = sortModel[0];
  const field = AG_GRID_COL_TO_SORT_FIELD[sort.colId];

  if (!field) {
    return undefined;
  }

  return {
    field,
    direction: sort.sort === "asc" ? "ASC" : "DESC",
  };
};

/**
 * Converts a DateRangeFilterModel to AccountingPayableDateRangeInput.
 * Returns undefined if neither startDate nor endDate is set.
 */
const toDateRangeInput = (
  model: DateRangeFilterModel | undefined,
): AccountingPayableDateRangeInput | undefined => {
  if (!model || (!model.startDate && !model.endDate)) {
    return undefined;
  }
  return {
    startDate: model.startDate ?? undefined,
    endDate: model.endDate ?? undefined,
  };
};

/**
 * Builds the GraphQL filters input from AG Grid's filter model.
 * Maps column filter values to the AccountingPayableFilters shape.
 * Returns undefined when no filters are active.
 */
export const buildAccountingPayableFilters = (
  filterModel?: AgGrid.FilterModel | null,
): AccountingPayableFilters | undefined => {
  if (!filterModel || Object.keys(filterModel).length === 0) {
    return undefined;
  }

  const filters: AccountingPayableFilters = {};

  // Client filter — sets a single arena ID
  const clientId = filterModel[SupportedColumns.ClientName] as string | undefined;
  if (clientId) {
    filters.arenaIds = [clientId];
  }

  // Entity filter — sets a single entity ID
  const entityId = filterModel[SupportedColumns.EntityName] as string | undefined;
  if (entityId) {
    filters.entityIds = [entityId];
  }

  // Payment type filter — multi-select string array
  const paymentTypes = filterModel[SupportedColumns.Type] as PaymentTypeFilterModel | undefined;
  if (paymentTypes && paymentTypes.length > 0) {
    filters.paymentTypes = paymentTypes;
  }

  // Transaction date range filter
  const transactionDateRange = toDateRangeInput(
    filterModel[SupportedColumns.TransactionDate] as DateRangeFilterModel | undefined,
  );
  if (transactionDateRange) {
    filters.transactionDateRange = transactionDateRange;
  }

  // Requested payment date range filter
  const requestedPaymentDateRange = toDateRangeInput(
    filterModel[SupportedColumns.RequestedPaymentDate] as DateRangeFilterModel | undefined,
  );
  if (requestedPaymentDateRange) {
    filters.requestedPaymentDateRange = requestedPaymentDateRange;
  }

  // Return undefined if no filters were actually populated
  return Object.keys(filters).length > 0 ? filters : undefined;
};

/**
 * AG Grid server-side datasource for the Transactions (Accounting Payables) table.
 *
 * Follows the same cursor-cache pattern as `DistributionBatchesGraphqlDataSource`:
 * - Maps AG Grid's offset-based pagination to cursor-based GraphQL pagination
 * - Caches endCursors keyed by endRow position for forward/backward navigation
 * - Falls back to `calculateCursorFromOffset` for page jumps
 * - Resets cursor cache when filters or sort change
 */
export class TransactionsGraphqlDataSource implements AgGrid.IServerSideDatasource {
  private client: ApolloClient<object>;
  private rowCountPerPage: number;

  // Cursor cache: maps endRow position → endCursor from the server response
  private cursors: Map<number, string> = new Map();

  // Reference copies for change detection (reset cursors when these change)
  private refFilters: AccountingPayableFilters | undefined;
  private refSort: AccountingPayableSort | undefined;

  constructor(client: ApolloClient<object>, rowCountPerPage: number) {
    this.client = client;
    this.rowCountPerPage = rowCountPerPage;
  }

  /**
   * Resets the cursor cache (called when filters or sorts change)
   */
  public resetCursors(): void {
    this.cursors.clear();
  }

  /**
   * Determines the cursor to use for pagination based on the requested start row.
   * First checks the cache, then calculates from offset if not found.
   */
  private getCursorForRow(startRow: number): string | undefined {
    if (startRow === 0) {
      return undefined;
    }

    const cachedCursor = this.cursors.get(startRow);
    if (cachedCursor) {
      return cachedCursor;
    }

    // User jumped to an arbitrary page — fall back to offset-based cursor
    return calculateCursorFromOffset(startRow - 1);
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const { api, request } = params;
    const { filterModel, sortModel, startRow = 0, endRow = this.rowCountPerPage } = request;

    api.showLoadingOverlay();

    const filters = buildAccountingPayableFilters(filterModel);
    const sort = buildAccountingPayableSort(sortModel);

    // Reset cursors when filters or sort changes
    if (!isEqual(this.refFilters, filters) || !isEqual(this.refSort, sort)) {
      this.resetCursors();
      this.refFilters = filters;
      this.refSort = sort;
    }

    const after = this.getCursorForRow(startRow);

    this.client
      .query<GetAccountingPayablesQuery>({
        query: GetAccountingPayablesDocument,
        context: clientContext(),
        fetchPolicy: "no-cache",
        variables: {
          filters,
          sort,
          first: this.rowCountPerPage,
          after,
        },
      })
      .then((response) => {
        const connection = response.data?.accountingPayables;
        const pageInfo = connection?.pageInfo;

        // Store the endCursor so the *next* page can use it
        if (pageInfo?.endCursor) {
          this.cursors.set(endRow, pageInfo.endCursor);
        }

        const rowData = (connection?.edges ?? []).map((edge) =>
          toTransactionRowItem(edge.node),
        );
        const count = connection?.count ?? 0;

        if (count === 0) {
          api.showNoRowsOverlay();
        } else {
          api.hideOverlay();
        }

        params.success({ rowData, rowCount: count });
      })
      .catch((e: Error) => {
        addError(formatError(e));
        toast.error(`Fetching transactions failed: ${e.message}`);
        api.hideOverlay();
        params.fail();
      });
  }
}
